import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { recipientsText } from '@/shared/emails/sending';
import { Badge } from '@/shared/ui/Badge';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Spinner } from '@/shared/ui/Spinner';
import { PaperclipIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';

import { formatWhen } from './dates';
import { mailboxLabel, type ConversationItem } from './types';
import { useConversation } from './useEmails';
import { emailHref } from './views';

/**
 * «Conversación (n)» in the detail (contract v10.42): the other messages of the thread,
 * oldest first, folded until the person opens it. Nothing for an email that is alone.
 *
 * What came in links to its own detail; what was sent from the platform says who sent it
 * and to whom, and what its owner sent from the mail client (read from the Sent folder,
 * v10.43) says so. The list is the API's: only the mailboxes this person sees, each message
 * once, and never a sending that failed (that one is under «Respuestas y reenvíos»).
 */
export function Conversation({ emailId, size }: { emailId: string; size: number }) {
  const [open, setOpen] = useState(false);
  const conversation = useConversation(emailId, open && size > 1);
  const [searchParams] = useSearchParams();
  if (size <= 1) return null;
  const items = conversation.data?.items ?? [];
  const severalMailboxes = new Set(items.map((item) => item.mailbox)).size > 1;
  return (
    <details
      className="border-line rounded-lg border"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="text-ink-2 hover:text-ink cursor-pointer px-4 py-3 text-[13.5px] font-medium select-none">
        Conversación ({size})
      </summary>
      <div className="border-line border-t px-4 py-3">
        {conversation.isPending && open ? (
          <Spinner label="Cargando la conversación" />
        ) : conversation.isError ? (
          <ErrorMessage error={conversation.error}>No se pudo cargar la conversación</ErrorMessage>
        ) : (
          <ol className="flex flex-col gap-2" aria-label="Mensajes de la conversación">
            {items.map((item) => (
              <li key={item.email_id ?? item.outgoing_id ?? item.at}>
                {item.kind === 'received' && item.email_id && !item.is_current ? (
                  <Link
                    to={emailHref(item.email_id, searchParams)}
                    className="group focus-visible:outline-accent block rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    <Message item={item} showMailbox={severalMailboxes} />
                  </Link>
                ) : (
                  <Message item={item} showMailbox={severalMailboxes} />
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </details>
  );
}

function Message({ item, showMailbox }: { item: ConversationItem; showMailbox: boolean }) {
  const sent = item.kind === 'sent';
  const fromMailClient = sent && item.origin === 'mail_client';
  const to = `a ${recipientsText(item.to ?? [])}`;
  const who = !sent
    ? (item.from_name ?? item.from_address ?? 'Remitente desconocido')
    : fromMailClient
      ? to
      : `${item.sent_by_name ?? 'Desde la plataforma'} ${to}`;
  const attachments = item.attachments ?? 0;
  return (
    <div
      className={cn(
        'border-line rounded-md border p-3 text-[13.5px]',
        item.is_current ? 'bg-paper-2' : 'bg-paper group-hover:bg-paper-2',
        sent && 'border-l-accent border-l-2',
      )}
      aria-current={item.is_current ? 'true' : undefined}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-ink min-w-0 font-medium break-words">
          {fromMailClient ? (
            <span className="text-ink-3 font-normal">Enviado desde el programa de correo </span>
          ) : sent ? (
            <span className="text-ink-3 font-normal">Enviado por </span>
          ) : null}
          {who}
        </span>
        <time dateTime={item.at} className="text-ink-4 shrink-0 text-[12.5px]">
          {formatWhen(item.at)}
        </time>
      </div>
      {item.snippet ? (
        <p className="text-ink-3 mt-1 line-clamp-2 break-words">{item.snippet}</p>
      ) : (
        <p className="text-ink-4 mt-1 italic">Sin texto</p>
      )}
      <div className="text-ink-4 mt-1.5 flex flex-wrap items-center gap-2 text-[12.5px]">
        {item.is_current ? <Badge tone="accent">Este correo</Badge> : null}
        {sent && item.status === 'pending' ? <Badge tone="info">Enviando</Badge> : null}
        {item.trashed ? <Badge>En la papelera</Badge> : null}
        {attachments > 0 ? (
          <span className="inline-flex items-center gap-1">
            <PaperclipIcon size={13} />
            {attachments === 1 ? '1 adjunto' : `${attachments} adjuntos`}
          </span>
        ) : null}
        {showMailbox ? (
          <span>
            {sent ? 'desde ' : 'en '}
            {mailboxLabel(item.mailbox)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
